import { ButtonHTMLAttributes, ReactNode } from 'react';
import {
  actionBtnDanger,
  actionBtnGhost,
  actionBtnOutline,
  actionBtnPrimary,
  actionBtnSuccess,
} from '@/utils/actionButtonClasses';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const actionVariantClasses: Record<ButtonVariant, string> = {
    primary: actionBtnPrimary,
    secondary: actionBtnOutline,
    success: actionBtnSuccess,
    danger: actionBtnDanger,
    warning: actionBtnOutline,
    ghost: actionBtnGhost,
    outline: actionBtnOutline,
  };

  const lgVariantClasses: Record<ButtonVariant, string> = {
    primary: 'btn-t-primary',
    secondary: 'btn-t-outline',
    success: 'btn-t-primary',
    danger: 'btn-t-primary',
    warning: 'btn-t-outline',
    ghost: 'btn-t-outline',
    outline: 'btn-t-outline',
  };

  const isLarge = size === 'lg';
  const combinedClasses = [
    isLarge ? 'btn-t' : actionVariantClasses[variant],
    isLarge ? lgVariantClasses[variant] : '',
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={combinedClasses} disabled={disabled || isLoading} {...props}>
      {isLoading && (
        <svg
          className="h-3.5 w-3.5 shrink-0 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!isLoading && leftIcon && <span className="shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{rightIcon}</span>}
    </button>
  );
}
